import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { NAATI_VISA_SUBCLASSES, findNaatiVisaSubclass } from "@/data/naati-visa-subclasses";
import { NAATI_DOCTYPES } from "@/data/naati-doctypes";
import NaatiVisaDeepContent from "@/components/NaatiVisaDeepContent";

export const Route = createFileRoute("/en/translate/naati/visa/$subclass")({
  loader: ({ params }) => {
    const v = findNaatiVisaSubclass(params.subclass);
    if (!v) throw notFound();
    const docs = v.requiredDoctypes
      .map((s) => NAATI_DOCTYPES.find((d) => d.slug === s))
      .filter((d): d is NonNullable<typeof d> => !!d);
    return { v, docs };
  },
  head: ({ params }) => {
    const v = findNaatiVisaSubclass(params.subclass);
    if (!v) return { meta: [{ title: ctaTitle("Not found", "/en/translate/naati/visa/$subclass", "en")}, { name: "robots", content: "noindex" }] };
    const path = `/en/translate/naati/visa/${v.slug}`;
    const TITLE = `NAATI Translation for ${v.nameEn} (Subclass ${v.code}) | NYC`;
    const DESC = `Thai documents you need NAATI-certified for ${v.nameEn} (Subclass ${v.code}): ${v.requiredDoctypes.slice(0, 5).join(", ")} · DHA processing ${v.processingTimeEn} · gov fee AUD ${v.govFeeAud.toLocaleString} — Get your checklist in minutes.`;
    const totalPrice = v.requiredDoctypes
      .map((s) => NAATI_DOCTYPES.find((d) => d.slug === s)?.priceFromThb ?? 0)
      .reduce((a, b) => a + b, 0);
    return {
      meta: [
        { title: ctaTitle(TITLE, "/en/translate/naati/visa/$subclass", "en")}, { name: "description", content: ctaDesc(DESC, "/en/translate/naati/visa/$subclass", "en")},
        { property: "og:title", content: ctaTitle(TITLE, "/en/translate/naati/visa/$subclass", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/translate/naati/visa/$subclass", "en")},
        { property: "og:url", content: `https://nyclanguageinstitute.com${path}` },
      ],
      links: canonicalAndHreflang(path, `/translate/naati/visa/${v.slug}`),
      scripts: buildLandingSchemas({
        path, title: TITLE, description: DESC,
        serviceType: `NAATI Translation — ${v.nameEn}`, priceFromThb: totalPrice,
        steps: [
          { name: "Gather source documents", text: `${v.requiredDoctypes.length} document types required by ${v.authority}` },
          { name: "NAATI translation + QR seal", text: "CPN-certified translator with digital seal for direct ImmiAccount upload" },
          { name: "Two-tier QA", text: "Editor + Reviewer sign off before release" },
          { name: "Delivery: PDF + hardcopy", text: "Free EMS Thailand + digitally sealed PDF" },
          { name: "Submit visa application", text: `DHA processing ${v.processingTimeEn}` },
        ],
        faqs: [
          { q: `What is Subclass ${v.code}?`, a: `${v.nameEn} — ${v.streamEn}` },
          { q: "How many documents need translation?", a: `${v.requiredDoctypes.length} core types — total approx ฿${totalPrice.toLocaleString()}` },
          { q: "Which authority reviews it?", a: v.authority },
          { q: "DHA processing time?", a: v.processingTimeEn },
          { q: "DHA government fee?", a: `AUD ${v.govFeeAud.toLocaleString()} (paid to DHA, separate from translation)` },
          { q: "Can I upload straight to ImmiAccount?", a: `Yes — every ${v.code} translation ships as a NAATI QR-sealed PDF ready for direct upload to ImmiAccount; no printing, no post, and no separate scan required. The QR links directly to the NAATI verification portal for the DHA case officer.` },
          { q: "What if the source document is older than 6 months?", a: `DHA requires civil-registry documents (birth, marriage, divorce, single-status) issued within the last 6 months. Re-issue from the local amphoe/district office first, then translate — we can courier and re-issue on your behalf in Bangkok so subclass ${v.code} isn't delayed.` },
          { q: "Workmanship guarantee?", a: `Free re-issue of any translation for subclass ${v.code} if DHA rejects it due to a translation error or NAATI stamp issue. Turnaround for a corrected re-issue is under 24 hours and we absorb DHL costs to your migration agent or ImmiAccount inbox.` },
        ],
        breadcrumbs: [
          { name: "Home", path: "/en" },
          { name: "NAATI", path: "/en/translate/naati" },
          { name: "By Visa", path: "/en/translate/naati/visa" },
          { name: `Subclass ${v.code}`, path },
        ],
      }),
    };
  },
  component: Page,
  notFoundComponent: () => (
    <main className="p-12"><h1 className="text-2xl font-bold">Subclass not found</h1></main>
  ),
});

function Page() {
  const { v, docs } = Route.useLoaderData() as { v: NonNullable<ReturnType<typeof findNaatiVisaSubclass>>; docs: typeof NAATI_DOCTYPES };
  const totalPrice = docs.reduce((a, b) => a + b.priceFromThb, 0);
  return (
    <>
      <ServiceLanding
        locale="en"
        badge={`NAATI · Subclass ${v.code}`}
        title={`NAATI Translation for ${v.nameEn}`}
        subtitle={`${v.streamEn} · Authority: ${v.authority} · Processing ${v.processingTimeEn}`}
        quickAnswer={{
          q: `What must Thai applicants translate for Subclass ${v.code}?`,
          a: `${v.requiredDoctypes.length} core Thai document types, translated by a CPN NAATI Certified Translator with a QR digital seal for ImmiAccount upload. Bundle approx ฿${totalPrice.toLocaleString()}, turnaround 2–3 business days (Express 24 h).`,
        }}
        heroBullets={[
          `✓ NAATI CPN Certified — accepted by ${v.authority}`,
          `✓ ${docs.length} document types covering every DHA checkpoint`,
          `✓ QR digital seal for direct ImmiAccount upload`,
          `✓ DHA processing: ${v.processingTimeEn}`,
        ]}
        whoFor={{
          th: [`${v.nameEn} applicants`, "Partners and dependents moving to AU", "Students, skilled workers, WHV holders"],
          foreign: [`Subclass ${v.code} — ${v.nameEn}`],
        }}
        steps={v.keyPointsEn.slice(0, 5).map((t, i) => ({ name: `Step ${i + 1}`, text: t }))}
        pricing={docs.map((d) => ({ label: d.nameEn, price: d.priceFromThb.toLocaleString(), note: d.turnaround }))}
        faqs={[
          { q: "Official source?", a: v.officialSource },
          { q: "Reviewing authority?", a: v.authority },
          { q: "Processing?", a: v.processingTimeEn },
          { q: "DHA fee?", a: `AUD ${v.govFeeAud.toLocaleString()}` },
          { q: "Approx translation bundle?", a: `฿${totalPrice.toLocaleString()} (base + NAATI stamp)` },
          ...v.keyPointsEn.map((k, i) => ({ q: `Key point ${i + 1}`, a: k })),
        ]}
        relatedLinks={[
          { to: "/en/translate/naati", label: "← NAATI hub" },
          { to: "/en/translate/naati/visa", label: "Other subclasses" },
          ...NAATI_VISA_SUBCLASSES.filter((x) => x.slug !== v.slug).slice(0, 4).map((x) => ({
            to: `/en/translate/naati/visa/${x.slug}`, label: `Subclass ${x.code} — ${x.nameEn}`,
          })),
          { to: "/en/apostille", label: "Apostille (Hague Dec 2025)" },
        ]}
      />
      <section className="mx-auto max-w-5xl px-4 py-10">
        <h2 className="text-2xl font-bold">Required documents ({docs.length})</h2>
        <div className="mt-4 overflow-x-auto rounded-2xl border">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr><th className="px-4 py-3">Document</th><th className="px-4 py-3">Price/page</th><th className="px-4 py-3">Turnaround</th><th className="px-4 py-3">Notes</th></tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.slug} className="border-t">
                  <td className="px-4 py-3 font-semibold">
                    <Link to="/en/translate/naati/doctype/$slug" params={{ slug: d.slug }} className="hover:text-amber-600">{d.nameEn}</Link>
                    <div className="text-xs font-normal text-slate-500">{d.nameTh}</div>
                  </td>
                  <td className="px-4 py-3">฿{d.priceFromThb.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs">{d.turnaround}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{d.notesEn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <NaatiVisaDeepContent v={v} totalPrice={totalPrice} lang="en" />
    </>
  );
}
